import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TOOL_INFO_CONTENT = {
  what: `Counts the words, characters, sentences, and paragraphs in a piece of text, with reading and speaking time and the words you use most — useful for meeting a word count requirement, checking if a social media post fits a character limit, or gauging how long a piece of writing actually is before submitting or publishing it.`,
  how: `Paste or type your text into the box, and the counts update automatically as you type or edit. Chinese and Japanese characters are counted one by one, the way Word and Google Docs count them. Speaking time assumes about 130 words a minute.`,
  tip: `Character counts usually matter more than word counts for social media posts specifically, since most platforms limit by character, not word count — a short sentence full of long words can hit a character limit well before it hits any reasonable word-count concern.`,
};

import { goalProgress, readingEase } from "./wordCounterExtras";
import { computeStats, WORKER_THRESHOLD_CHARS, formatReadingTime, formatSpeakingTime, topKeywords, type TextStats } from "./wordCounterCore";

const WordCounterExtras = lazy(() => import("./WordCounterExtras"));

// Existing callers and tests import these from here.
export { countWords, countSentences, countGraphemes, formatReadingTime } from "./wordCounterCore";

// A trailing-edge debounce for the text itself, not the derived counts --
// keeps every keystroke responsive in the textarea (React state updates
// instantly either way) while the heavier counting passes only run once
// typing actually pauses. Measured directly, not assumed: the new
// counting functions above run a 1.68 MB / 300k-word input in ~250ms
// combined (down from ~3.9s before this fix), so this is a light
// debounce for comfort on very large pastes, not a strict requirement.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}


const EMPTY_STATS: TextStats = { words: 0, chars: 0, charsNoSpace: 0, sentences: 0, paragraphs: 0 };

// Counts synchronously for ordinary text; above WORKER_THRESHOLD_CHARS counts in a Web Worker
// so a huge paste can't freeze the page, showing "Counting…" until the answer arrives. A newer
// text always wins: an answer for text that has since changed is discarded.
function useTextStats(text: string): { stats: TextStats; counting: boolean } {
  const big = text.length > WORKER_THRESHOLD_CHARS;
  const small = useMemo(() => (big ? null : computeStats(text)), [text, big]);
  const [workerStats, setWorkerStats] = useState<{ forText: string; stats: TextStats } | null>(null);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (!big) return;
    if (!workerRef.current) {
      workerRef.current = new Worker(new URL("./wordCounter.worker.ts", import.meta.url), { type: "module" });
    }
    const worker = workerRef.current;
    let current = true;
    const onMessage = (e: MessageEvent<{ stats: TextStats }>) => {
      if (current) setWorkerStats({ forText: text, stats: e.data.stats });
    };
    worker.addEventListener("message", onMessage);
    worker.postMessage({ text });
    return () => {
      current = false;
      worker.removeEventListener("message", onMessage);
    };
  }, [text, big]);

  useEffect(() => () => { workerRef.current?.terminate(); workerRef.current = null; }, []);

  if (!big) return { stats: small ?? EMPTY_STATS, counting: false };
  const ready = workerStats && workerStats.forText === text;
  return { stats: ready ? workerStats.stats : EMPTY_STATS, counting: !ready };
}

export default function WordCounter() {
  const [text, setText] = useState("");
  const debouncedText = useDebounced(text, 200);
  const { stats, counting } = useTextStats(debouncedText);
  const [goal, setGoal] = useState("");
  const [includeCommon, setIncludeCommon] = useState(false);

  // The live counter never takes a trial use: typing makes no file. A use is counted when the stats are copied out.
  const { logUsageNow } = useLogUsageOnAction();
  const keywords = useMemo(() => (debouncedText.length > WORKER_THRESHOLD_CHARS ? [] : topKeywords(debouncedText, 10, includeCommon)), [debouncedText, includeCommon]);
  const show = (v: string | number) => (counting ? "…" : v);
  const copyStats = async () => {
    if (!(await logUsageNow("download"))) return;
    const big = debouncedText.length > WORKER_THRESHOLD_CHARS;
    const ease = big ? null : readingEase(debouncedText, stats.sentences);
    const gp = goalProgress(stats.words, Number(goal));
    const lines = [
      `Words: ${stats.words}`, `Characters: ${stats.chars}`, `Characters (no spaces): ${stats.charsNoSpace}`,
      `Sentences: ${stats.sentences}`, `Paragraphs: ${stats.paragraphs}`, `Reading time: ${formatReadingTime(stats.words)}`, `Speaking time: ${formatSpeakingTime(stats.words)}`,
      ...(ease?.applicable ? [`Reading ease: ${ease.flesch} (${ease.label}), Flesch-Kincaid grade ${ease.grade}`] : []),
      ...(gp ? [`Word goal: ${gp.words} of ${gp.target} (${gp.percent}%) - ${gp.text}`] : []),
      ...(keywords.length ? [`Top keywords: ${keywords.map((k) => `${k.word} (${k.count})`).join(", ")}`] : []),
    ];
    try { await navigator.clipboard.writeText(lines.join("\n")); toast.success("Stats copied"); }
    catch { toast.error("Couldn't copy. Select the numbers and copy them by hand."); }
  };

  return (
    <ToolContainer>
      <Textarea aria-label="Text to count" placeholder="Type or paste your text here..." value={text} onChange={e => setText(e.target.value)} className="min-h-[200px] text-base" />
      <div className="flex gap-2 mt-3">
        <Button type="button" variant="outline" size="sm" onClick={() => setText("")} disabled={!text}>Clear</Button>
        <Button type="button" variant="outline" size="sm" onClick={copyStats} disabled={!text.trim()}>Copy stats</Button>
      </div>
      {counting && <p className="text-xs text-muted-foreground mt-3" role="status">Counting…</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        {[
          { label: "Words", value: show(stats.words) },
          { label: "Characters", value: show(stats.chars) },
          { label: "Characters (no spaces)", value: show(stats.charsNoSpace) },
          { label: "Sentences", value: show(stats.sentences) },
          { label: "Paragraphs", value: show(stats.paragraphs) },
          { label: "Reading Time", value: show(formatReadingTime(stats.words)) },
          { label: "Speaking Time", value: show(formatSpeakingTime(stats.words)) },
        ].map(item => (
          <div key={item.label} className="p-4 rounded-lg border bg-card text-center">
            <p className="text-2xl font-bold text-primary">{item.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{item.label}</p>
          </div>
        ))}
      </div>
      {(keywords.length > 0 || includeCommon) && !counting && (
        <div className="mt-6" data-testid="keyword-density">
          <h2 className="text-sm font-semibold">Top keywords</h2>
          <p className="text-xs text-muted-foreground mt-1">{includeCommon ? "Most used words, including common ones like \"the\" and \"and\"." : "Most used words, leaving out common ones like \"the\" and \"and\"."} Percent is the share of all words.</p>
          <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={includeCommon} onChange={(e) => setIncludeCommon(e.target.checked)} /> Include common words</label>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {keywords.map((k) => (
              <li key={k.word} className="flex justify-between border-b py-1"><span className="font-medium break-all">{k.word}</span><span className="text-muted-foreground">{k.count} &middot; {k.percent}%</span></li>
            ))}
          </ul>
        </div>
      )}
      <Suspense fallback={null}>
        <WordCounterExtras text={debouncedText} words={stats.words} chars={stats.chars} sentences={stats.sentences} counting={counting} tooBig={debouncedText.length > WORKER_THRESHOLD_CHARS} goal={goal} onGoalChange={setGoal} />
      </Suspense>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
